import { useState } from 'react'
import { isHTTPError } from 'ky'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { AlertCircle, UserCog } from 'lucide-react'
import type { UserListItem, UpdateUserRoleResponse, AssignableRole } from '@common/types'
import { ROLE_LABELS } from '@common/permissions'
import { updateUserRole } from '@frontend/lib/api'
import { usePermissions } from '@frontend/hooks/usePermissions'
import { RoleBadge } from '@frontend/components/ui/role-badge'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@frontend/components/ui/dialog'
import { Button } from '@frontend/components/ui/button'
import { Spinner } from '@frontend/components/ui/spinner'

interface UpdateRoleModalProps {
  user: UserListItem | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function UpdateRoleModal({ user, open, onOpenChange }: UpdateRoleModalProps) {
  const queryClient = useQueryClient()
  const { getAssignableRoles } = usePermissions()

  const assignableRoles = getAssignableRoles()

  // Form state — initialized once; parent remounts this component per user via `key`.
  const currentRole = user?.role as AssignableRole | undefined
  const [newRole, setNewRole] = useState<AssignableRole>(
    currentRole && assignableRoles.includes(currentRole) ? currentRole : assignableRoles[0] || 'basic_user'
  )
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)

  const updateMutation = useMutation({
    mutationFn: () => updateUserRole(user!.userId, { role: newRole, reason: reason.trim() || undefined }),
    onSuccess: (data: UpdateUserRoleResponse) => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      onOpenChange(false)
      toast.success(`Role updated to ${ROLE_LABELS[data.user.role]}`)
    },
    onError: (err) => {
      if (isHTTPError(err)) {
        const data = err.data as { error?: string }
        setError(data.error || 'Failed to update role')
      } else {
        setError('Failed to update role')
      }
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!user) return

    if (newRole === user.role) {
      setError('Please select a different role')
      return
    }

    updateMutation.mutate()
  }

  const isSubmitting = updateMutation.isPending

  if (!user) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCog className="h-5 w-5" />
            Change Role
          </DialogTitle>
          <DialogDescription>Update the role for {user.email}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Current role display */}
          <div>
            <span className="block text-sm font-medium mb-1">Current Role</span>
            <RoleBadge role={user.role} />
          </div>

          {/* New role selection */}
          <div>
            <label htmlFor="newRole" className="block text-sm font-medium mb-1">
              New Role <span className="text-red-500">*</span>
            </label>
            <select
              id="newRole"
              value={newRole}
              onChange={(e) => setNewRole(e.target.value as AssignableRole)}
              className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background"
              disabled={isSubmitting}
            >
              {assignableRoles.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r]}
                </option>
              ))}
            </select>
          </div>

          {/* Reason (optional) */}
          <div>
            <label htmlFor="reason" className="block text-sm font-medium mb-1">
              Reason <span className="text-muted-foreground">(optional)</span>
            </label>
            <textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              placeholder="Why are you changing this user's role?"
              rows={2}
              maxLength={500}
              disabled={isSubmitting}
            />
            <p className="text-xs text-muted-foreground mt-1">This will be recorded in the audit log.</p>
          </div>

          {/* Error display */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-md text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || newRole === user.role}>
              {isSubmitting ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Updating...
                </>
              ) : (
                'Update Role'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
